import { Link } from "react-router-dom";

const EFFECTIVE = "January 2026";

export default function TermsConditions() {
  return (
    <section className="bg-offwhite py-16 sm:py-24" data-testid="terms-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <p className="text-money font-bold uppercase tracking-widest text-sm mb-3">Aspire Collective Partners LLC</p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mb-3">Terms and Conditions</h1>
        <p className="text-sm text-muted mb-10">Effective {EFFECTIVE}</p>

        <article className="prose-article space-y-8 text-navy-800 text-base leading-relaxed">
          <Section title="1. Acceptance of these terms">
            <p>
              These Terms and Conditions (the &ldquo;Terms&rdquo;) govern your access to and use of the Aspire Collective Partners LLC website and any services offered through it. By using this site, submitting information through any lead form, or communicating with us as a result of this site, you agree to these Terms. If you do not agree, please do not use the site.
            </p>
          </Section>

          <Section title="2. Who we are">
            <p>
              Aspire Collective Partners LLC is a U.S. based real-estate company that buys single-family homes and small residential properties directly from homeowners for cash. We may act as a principal buyer, a wholesaler assigning the purchase contract to a partner buyer, or a facilitator connecting a seller to another cash buyer, depending on the transaction.
            </p>
          </Section>

          <Section title="3. Not a real-estate agent or brokerage">
            <p>
              Aspire Collective Partners LLC is <strong>not</strong> a licensed real-estate brokerage, and we are not acting as your agent, broker, attorney, accountant, or financial advisor. Nothing on this site is legal, tax, or financial advice. You are strongly encouraged to consult independent professionals before signing any contract, deed, or other document.
            </p>
          </Section>

          <Section title="4. How our cash offer process works">
            <p>When you submit a lead form we may, in our sole discretion:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Review public property data and comparable sales in your area;</li>
              <li>Contact you by phone, text, or email to ask follow-up questions;</li>
              <li>Schedule an inspection or drive-by of the property;</li>
              <li>Present a written cash offer, subject to inspection and clear title;</li>
              <li>Decline to make an offer, for any reason and without explanation.</li>
            </ul>
            <p>
              Any cash offer is a good-faith estimate of what we or a partner buyer would pay for the property and is <strong>not a binding contract</strong> until (a) a written purchase agreement is signed by both parties and (b) any inspection contingencies are satisfied or waived. Estimated offer amounts communicated over the phone, in email, or in text are non-binding until a signed contract exists. Aspire Collective Partners LLC reserves the right to withdraw or revise any offer at any time before a signed contract, including after inspection or title review.
            </p>
          </Section>

          <Section title="5. No obligation, no fees">
            <p>
              You are under no obligation to accept any cash offer we present. We do not charge sellers commissions or fees for reviewing their property or making an offer. If a transaction closes, we typically pay standard closing costs — the specific costs will be spelled out in the purchase agreement.
            </p>
          </Section>

          <Section title="6. Your representations">
            <p>By submitting a lead form, you represent that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are at least 18 years old and able to enter into a binding contract;</li>
              <li>You are either the owner of the property, an authorized executor or trustee, or otherwise authorized to discuss selling it;</li>
              <li>The information you provide is accurate to the best of your knowledge;</li>
              <li>You have read our Privacy Policy and consent to the collection and use of your information as described there.</li>
            </ul>
          </Section>

          <Section title="7. Communications and text messages">
            <p>
              By submitting a lead form and checking the required consent box, you agree that Aspire Collective Partners LLC and its service providers may contact you at the phone number and email address you provided — even if the phone number is on a state or federal do-not-call list — for purposes related to your inquiry. You can opt out of text messages at any time by replying STOP; you can opt out of marketing emails by using the unsubscribe link in any marketing email. Standard message and data rates may apply.
            </p>
          </Section>

          <Section title="8. Content and intellectual property">
            <p>
              All content on this site, including text, graphics, logos, and the Aspire Collective Partners LLC name and mark, is owned by Aspire Collective Partners LLC or its licensors and is protected by U.S. and international intellectual-property laws. You may view and print pages for your personal, non-commercial use. You may not copy, republish, sell, or otherwise exploit the content without our written permission.
            </p>
          </Section>

          <Section title="9. Acceptable use">
            <p>You agree not to (a) use the site or forms to submit false information, someone else&rsquo;s information without permission, or spam; (b) attempt to interfere with the site&rsquo;s operation or security; (c) scrape, crawl, or bulk-download the site for commercial use; (d) use the site for anything unlawful.</p>
          </Section>

          <Section title="10. Third-party links and services">
            <p>
              The site may link to third-party websites or use third-party services (for example, Google Maps address autocomplete, Resend email delivery, or Twilio SMS delivery). We are not responsible for third-party sites or content and their inclusion does not imply endorsement. Use of any third-party service is at your own risk and subject to that provider&rsquo;s terms.
            </p>
          </Section>

          <Section title="11. Disclaimer of warranties">
            <p>
              The site and any information, offer estimates, or communications we provide are made available on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis, with no warranties of any kind, express or implied. To the fullest extent permitted by law, we disclaim all warranties, including of merchantability, fitness for a particular purpose, non-infringement, and any implied warranty arising from course of dealing or performance.
            </p>
          </Section>

          <Section title="12. Limitation of liability">
            <p>
              To the fullest extent permitted by applicable law, Aspire Collective Partners LLC and its owners, employees, agents, and affiliates will not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the site, our communications, or any offer or non-offer, even if we have been advised of the possibility of such damages. Our aggregate liability for any claim arising out of or relating to the site or these Terms will not exceed one hundred U.S. dollars (US$100). Some states do not allow the exclusion or limitation of certain damages, so parts of this section may not apply to you.
            </p>
          </Section>

          <Section title="13. Indemnification">
            <p>
              You agree to defend, indemnify, and hold harmless Aspire Collective Partners LLC from any claim, loss, or expense (including reasonable attorney&rsquo;s fees) arising out of (a) your use of the site, (b) any information you submit that is untrue or infringes another person&rsquo;s rights, or (c) your breach of these Terms.
            </p>
          </Section>

          <Section title="14. Governing law and disputes">
            <p>
              These Terms are governed by the laws of the State of Ohio, without regard to conflict-of-laws principles. Any dispute arising under these Terms will be resolved exclusively in the state or federal courts located in Franklin County, Ohio, and you consent to personal jurisdiction there. Nothing in this section prevents either party from seeking injunctive relief in any court of competent jurisdiction.
            </p>
          </Section>

          <Section title="15. Changes to these Terms">
            <p>
              We may update these Terms from time to time. When we do, we will change the effective date at the top of this page. Continued use of the site after an update constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="16. Contact">
            <p>
              Questions about these Terms? Email <a href="mailto:carlos@aspirecollectivepartnersllc.com" className="text-money font-semibold">carlos@aspirecollectivepartnersllc.com</a> or call <a href="tel:+16149664283" className="text-money font-semibold">(614) 966-4283</a>.
            </p>
          </Section>
        </article>

        <div className="mt-14 text-center">
          <Link to="/" className="text-money font-semibold" data-testid="terms-home-link">← Back to home</Link>
        </div>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-navy mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
